import { useEffect, useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { StatusPill } from "@/components/StatusPill";
import { useAppointments } from "@/hooks/useAppointments";
import { useStaff, useServices } from "@/hooks/useCatalog";
import { useBranch } from "@/contexts/BranchContext";
import { useSettings } from "@/contexts/SettingsContext";
import {
  Search, Filter, X, ChevronDown, ChevronRight, AlertOctagon, UserPlus, Send,
  PhoneForwarded, Megaphone, BellRing, Check, CalendarClock, Ban,
} from "lucide-react";
import { toast } from "sonner";
import { RECEPTIONIST, RECEPTIONIST_EXTRA } from "@/constants/testIds";
import { api, unwrap } from "@/lib/api";
import { BirthdaySection } from "@/components/BirthdaySection";
import { ClientLookupBar } from "@/components/ClientLookupBar";
import { ClientProfilePanel } from "@/components/ClientProfilePanel";
import { MembershipEnrolModal } from "@/components/MembershipEnrol";
import { MemberBadge } from "@/components/MemberBadge";
import { Sparkles, History, Zap } from "lucide-react";
import "@/components/membership.css";

const NEXT_STATES = {
  "booked":          ["checked-in", "no-show", "cancelled"],
  "checked-in":      ["in-service", "no-show"],
  "in-service":      ["payment-pending", "completed"],
  "payment-pending": ["completed", "in-service"],
  "completed":       [],
  "no-show":         ["booked"],
  "cancelled":       ["booked"],
};

const STATUS_ORDER = ["in-service", "checked-in", "booked", "payment-pending", "completed", "no-show", "cancelled"];
const STATUS_LABEL = {
  "in-service": "In Service",
  "checked-in": "Checked In",
  "booked": "Booked · Upcoming",
  "payment-pending": "Payment Pending",
  "completed": "Completed",
  "no-show": "No Show",
  "cancelled": "Cancelled",
};

function elapsedMinSec(startedAt) {
  if (!startedAt) return "00:00:00";
  const ms = Date.now() - new Date(startedAt).getTime();
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600).toString().padStart(2, "0");
  const m = Math.floor((totalSec % 3600) / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function Receptionist() {
  const { activeBranchId } = useBranch();
  const { items, update, create, refresh } = useAppointments({ branchId: activeBranchId });
  const { staff } = useStaff();
  const { services } = useServices();

  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(new Set(STATUS_ORDER));
  const [openModal, setOpenModal] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [collapsed, setCollapsed] = useState({ completed: true, "no-show": true, cancelled: true, "payment-pending": false });
  const [tick, setTick] = useState(0);
  const [profileClientId, setProfileClientId] = useState(null);
  const [enrolClient, setEnrolClient] = useState(null);
  const [prefillPhone, setPrefillPhone] = useState(null);

  useEffect(() => { const i = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(i); }, []);

  // Lookup memberships for clients currently in the queue
  const [memberByClientId, setMemberByClientId] = useState({});
  useEffect(() => {
    const ids = Array.from(new Set((items || []).map((a) => a.client_id).filter(Boolean)));
    if (ids.length === 0) return;
    let stale = false;
    api.get("/memberships").then((r) => {
      if (stale) return;
      const list = unwrap(r) || [];
      const map = {};
      for (const m of list) {
        if (m.is_active && m.color_scheme) map[m.client_id] = m;
      }
      setMemberByClientId(map);
    }).catch(() => {});
    return () => { stale = true; };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((a) => {
      if (!filters.has(a.status)) return false;
      if (!q) return true;
      return (
        a.client_name?.toLowerCase().includes(q) ||
        a.token_number?.toLowerCase().includes(q) ||
        a.client_phone?.includes(q) ||
        (a.services_requested || []).some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [items, filters, search]);

  const groups = useMemo(() => {
    const map = {};
    for (const s of STATUS_ORDER) map[s] = [];
    for (const a of filtered) map[a.status]?.push(a);
    return map;
  }, [filtered]);

  const kpis = useMemo(() => ({
    inQueue: items.filter((a) => ["booked", "checked-in"].includes(a.status)).length,
    checkedIn: items.filter((a) => a.status === "checked-in").length,
    inService: items.filter((a) => a.status === "in-service").length,
    completed: items.filter((a) => a.status === "completed").length,
  }), [items]);

  const activeInService = items.find((a) => a.status === "in-service");

  const setStatus = async (id, status) => {
    try {
      await update(id, { status });
      toast.success(`Status updated`, { description: `Moved to ${status.replace("-", " ")}` });
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not update status");
    }
  };

  const checkInNext = async () => {
    const next = items.find((a) => a.status === "booked");
    if (!next) { toast.info("No bookings waiting to be checked in"); return; }
    await setStatus(next.id, "checked-in");
  };

  const autoPromote = async () => {
    const cancelled = items.find((a) => a.status === "cancelled");
    if (!cancelled) { toast.info("No cancellations to promote from"); return; }
    await update(cancelled.id, { status: "booked" });
    try {
      await api.post("/notifications/log", {
        type: "waitlist_promote",
        client_id: cancelled.client_id,
        client_name: cancelled.client_name,
        client_phone: cancelled.client_phone,
        message: `Waitlist promote · ${cancelled.token_number}`,
        channel: "whatsapp", status: "mocked",
        branch_id: activeBranchId,
      });
    } catch {}
    toast.success("Waitlist promoted", { description: `Sent WhatsApp to ${cancelled.client_name} — reply YES within 10 minutes.` });
  };

  const cancelAppointment = async (id, reason) => {
    try {
      await api.post(`/appointments/${id}/cancel`, { reason: reason || null });
      await refresh();
      toast.success("Appointment cancelled", { description: "Client notified on WhatsApp." });
      setCancelTarget(null);
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not cancel appointment");
    }
  };

  const rescheduleAppointment = async (id, payload) => {
    try {
      await api.post(`/appointments/${id}/reschedule`, payload);
      await refresh();
      toast.success("Appointment rescheduled", { description: `New time: ${payload.new_date} at ${payload.new_time}` });
      setRescheduleTarget(null);
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not reschedule", { description: data?.code || "" });
    }
  };

  return (
    <RoleShell title="Receptionist" showFloorMap rightExtras={null}>
      <div className="max-w-[1700px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-testid={RECEPTIONIST.root}>
        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="glass-primary rounded-[20px] p-5">
            <div className="overline mb-3">Today at a glance</div>
            <div className="grid grid-cols-2 gap-3">
              <KpiTile label="In Queue"    value={kpis.inQueue}    accent="#B5892E"   testid={RECEPTIONIST.kpiInQueue} />
              <KpiTile label="Checked In"  value={kpis.checkedIn}  accent="#B45309"   testid={RECEPTIONIST.kpiCheckedIn} />
              <KpiTile label="In Service"  value={kpis.inService}  accent="#5E4060"   testid={RECEPTIONIST.kpiInService} />
              <KpiTile label="Completed"   value={kpis.completed}  accent="#15803D"   testid={RECEPTIONIST.kpiCompleted} />
            </div>
          </div>

          <div className="glass-primary rounded-[20px] p-5">
            <div className="overline mb-3">Quick actions</div>
            <div className="flex flex-col gap-2.5">
              <button className="btn-3d btn-gold-3d justify-start" onClick={() => setOpenModal("add")} data-testid={RECEPTIONIST.addClientBtn}>
                <UserPlus size={16} /> Add Client
              </button>
              <button className="btn-3d btn-mauve-3d justify-start" onClick={checkInNext} data-testid={RECEPTIONIST.checkInNextBtn}>
                <PhoneForwarded size={16} /> Check In Next
              </button>
              <button className="btn-3d btn-glass-3d justify-start" onClick={() => setOpenModal("broadcast")} data-testid={RECEPTIONIST.broadcastBtn}>
                <Megaphone size={16} /> Broadcast Delay
              </button>
              <button className="btn-3d btn-danger-3d justify-start" onClick={() => setOpenModal("emergency")} data-testid={RECEPTIONIST.emergencyBtn}>
                <AlertOctagon size={16} /> Emergency Walk-In
              </button>
              <button className="btn-3d btn-glass-3d justify-start relative" onClick={autoPromote} data-testid={RECEPTIONIST.waitlistBtn}>
                <BellRing size={16} /> Waitlist Auto-Promote
                {items.find((a) => a.status === "cancelled") && (
                  <span className="absolute top-1.5 right-3 w-2 h-2 rounded-full bg-[var(--color-warning)] live-dot" />
                )}
              </button>
            </div>
          </div>

          {activeInService && (
            <div className="rounded-[20px] p-5 glass-elevated relative overflow-hidden" style={{ borderLeft: "4px solid var(--color-mauve)" }}>
              <div className="overline mb-2">Active service</div>
              <div className="font-display font-semibold text-[18px] text-[var(--color-text-primary)]">{activeInService.client_name}</div>
              <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">
                {(activeInService.services_requested || []).join(" + ")}
              </div>
              <div className="font-inter text-[12.5px] text-[var(--color-text-secondary)] mt-1">
                with {activeInService.assigned_stylist_name} · {activeInService.chair_room}
              </div>
              <div className="font-mono text-[26px] text-[var(--color-mauve-deep)] mt-3 leading-none">
                {elapsedMinSec(activeInService.service_started_at)}
              </div>
            </div>
          )}
        </aside>

        {/* Main */}
        <section>
          {/* Client Lookup — primary action above the queue */}
          <div className="mb-4">
            <ClientLookupBar
              onSelectClient={(c) => setProfileClientId(c.id)}
              onAddNew={(phone) => { setPrefillPhone(phone); setOpenModal("add"); }}
            />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="overline mb-1">Live queue</div>
              <h1 className="font-display font-bold text-[28px] tracking-[-0.02em] flex items-center gap-3 flex-wrap">
                Today, {new Date().toLocaleDateString([], { month: "long", day: "numeric" })}
                <span className="px-3 py-1 rounded-full glass-subtle font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)]">
                  {items.length} appointments
                </span>
              </h1>
            </div>

            <div className="flex items-center gap-2 relative">
              <div className="flex items-center h-11 w-[280px] glass-subtle rounded-full px-4">
                <Search size={16} className="text-[var(--color-text-muted)]" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search token, name, phone…" className="bg-transparent flex-1 px-3 font-inter text-[13.5px] outline-none" />
                {search && <button onClick={() => setSearch("")}><X size={14} /></button>}
              </div>
              <button className="btn-3d btn-glass-3d btn-sm" onClick={() => setFilterOpen((x) => !x)}>
                <Filter size={14} /> Filters {filters.size < STATUS_ORDER.length && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--color-gold-mid)] text-white text-[10px] font-bold">{filters.size}</span>}
              </button>
              {filterOpen && (
                <div className="absolute right-0 top-12 z-30 glass-elevated rounded-2xl p-3 w-64 anim-slide-up">
                  <div className="overline mb-2 px-1">Show statuses</div>
                  {STATUS_ORDER.map((s) => (
                    <label key={s} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/40 cursor-pointer">
                      <input type="checkbox" checked={filters.has(s)} onChange={() => {
                        const n = new Set(filters);
                        if (n.has(s)) n.delete(s); else n.add(s);
                        setFilters(n);
                      }} />
                      <StatusPill status={s} />
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Grouped table */}
          <div className="space-y-4">
            {STATUS_ORDER.map((status) => {
              const list = groups[status] || [];
              if (list.length === 0) return null;
              const isCollapsed = collapsed[status];
              return (
                <div key={status} className="glass-primary rounded-[20px] overflow-hidden">
                  <button onClick={() => setCollapsed((c) => ({ ...c, [status]: !c[status] }))} className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/30 transition">
                    <div className="flex items-center gap-3">
                      <StatusPill status={status} />
                      <span className="font-sora font-semibold text-[14px] text-[var(--color-text-secondary)]">{STATUS_LABEL[status]} · {list.length}</span>
                    </div>
                    {isCollapsed ? <ChevronRight size={16} className="text-[var(--color-text-muted)]" /> : <ChevronDown size={16} className="text-[var(--color-text-muted)]" />}
                  </button>
                  {!isCollapsed && (
                    <div>
                      {list.map((a) => (
                        <AppointmentRow
                          key={a.id}
                          appointment={a}
                          expanded={expanded[a.id]}
                          onToggle={() => setExpanded((e) => ({ ...e, [a.id]: !e[a.id] }))}
                          onSetStatus={(s) => setStatus(a.id, s)}
                          onAssign={(field, value) => update(a.id, { [field]: value })}
                          onRequestCancel={() => setCancelTarget(a)}
                          onRequestReschedule={() => setRescheduleTarget(a)}
                          onOpenProfile={a.client_id ? () => setProfileClientId(a.client_id) : null}
                          memberInfo={memberByClientId[a.client_id]}
                          staff={staff}
                          services={services}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="glass-primary rounded-2xl p-10 text-center">
                <div className="font-display text-[22px] font-semibold mb-2">A quiet moment ✨</div>
                <div className="font-inter text-[14px] text-[var(--color-text-muted)]">No appointments match the current filter.</div>
              </div>
            )}
          </div>
        </section>
      </div>

      {openModal === "add" && <AddClientModal services={services} staff={staff} prefillPhone={prefillPhone} onClose={() => { setOpenModal(null); setPrefillPhone(null); }} onCreate={async (p) => { await create(p); toast.success("Added to queue"); setOpenModal(null); setPrefillPhone(null); }} />}
      {openModal === "emergency" && <EmergencyModal staff={staff} onClose={() => setOpenModal(null)} onCreate={async (p) => { await create({ ...p, is_emergency: true }); toast.success("Emergency placed at top of queue"); setOpenModal(null); }} />}
      {openModal === "broadcast" && <BroadcastModal items={items} onClose={() => setOpenModal(null)} />}
      {cancelTarget && (
        <CancelModal
          appointment={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={(reason) => cancelAppointment(cancelTarget.id, reason)}
        />
      )}
      {rescheduleTarget && (
        <RescheduleModal
          appointment={rescheduleTarget}
          staff={staff}
          branchId={activeBranchId}
          onClose={() => setRescheduleTarget(null)}
          onConfirm={(payload) => rescheduleAppointment(rescheduleTarget.id, payload)}
        />
      )}

      {/* Client Profile Panel (right slide-in) */}
      {profileClientId && (
        <ClientProfilePanel
          clientId={profileClientId}
          onClose={() => setProfileClientId(null)}
          onCheckIn={async (appId) => {
            try {
              await update(appId, { status: "checked-in" });
              toast.success("Checked in");
            } catch {
              toast.error("Check-in failed");
            }
          }}
          onBookNow={() => { toast.info("Public booking opens at /book"); }}
          onEnrolMembership={(c) => { setProfileClientId(null); setEnrolClient(c); }}
          onProductSale={(c) => { window.open("/products", "_blank"); }}
          onUpdated={() => refresh()}
        />
      )}

      {/* Membership Enrolment Modal */}
      {enrolClient && (
        <MembershipEnrolModal
          client={enrolClient}
          onClose={() => setEnrolClient(null)}
          onEnrolled={() => { refresh(); }}
        />
      )}
    </RoleShell>
  );
}

function KpiTile({ label, value, accent, testid }) {
  return (
    <div className="rounded-2xl p-3.5 bg-white/55 border border-white/80" data-testid={testid}>
      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] uppercase tracking-[0.1em] font-medium mb-1">{label}</div>
      <div className="font-mono text-[30px] leading-none" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function AppointmentRow({ appointment: a, expanded, onToggle, onSetStatus, onAssign, onRequestCancel, onRequestReschedule, onOpenProfile, staff, services, memberInfo }) {
  const validNext = NEXT_STATES[a.status] || [];
  const isEmergency = a.is_emergency;
  const canCancel = !["completed", "cancelled"].includes(a.status);
  const canReschedule = !["completed"].includes(a.status);
  const memberStyle = memberInfo
    ? { borderLeft: "3px solid #D4A853" }
    : (isEmergency ? { background: "rgba(185,28,28,0.04)", borderLeft: "3px solid #B91C1C" } : {});
  return (
    <div
      className="border-t border-white/40 transition"
      style={memberStyle}
      data-testid={RECEPTIONIST.appointmentRow(a.id)}
    >
      <div
        className="px-5 py-3.5 flex items-center gap-4 hover:bg-white/30 cursor-pointer"
        onClick={onToggle}
        style={{ height: 60 }}
        role="button"
        data-testid={`appt-row-header-${a.id}`}
      >
        <div className={`font-mono text-[18px] min-w-[64px] ${isEmergency ? "text-[var(--color-danger)] font-medium" : "text-[var(--color-mauve-deep)]"}`}>{a.token_number}</div>
        <div className="flex-1 min-w-0">
          <div className="font-sora font-semibold text-[14.5px] text-[var(--color-text-primary)] truncate flex items-center gap-2 flex-wrap">
            {a.client_name}
            {memberInfo && <MemberBadge membership={memberInfo} />}
            {isEmergency && <span className="status-pill" style={{ background: "rgba(185,28,28,0.10)", color: "#991B1B" }}><span className="dot" style={{ background: "#B91C1C", animation: "pulseDot 1.4s infinite" }} />EMERGENCY</span>}
          </div>
          <div className="font-inter text-[12px] text-[var(--color-text-muted)] truncate">
            {(a.services_requested || []).join(" + ")} {a.assigned_stylist_name && <>· {a.assigned_stylist_name}</>} {a.chair_room && <>· {a.chair_room}</>}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <select
            value={a.status}
            onChange={(e) => onSetStatus(e.target.value)}
            className="text-[12.5px] font-sora font-semibold uppercase tracking-wider rounded-full px-3 py-1.5 border border-white/80 bg-white/70 outline-none"
            data-testid={RECEPTIONIST.statusSelect(a.id)}
          >
            <option value={a.status}>{a.status.replace("-", " ")}</option>
            {validNext.map((s) => <option key={s} value={s}>{s.replace("-", " ")}</option>)}
          </select>
        </div>
        <ChevronDown size={16} className={`text-[var(--color-text-muted)] transition ${expanded ? "rotate-180" : ""}`} />
      </div>

      {expanded && (
        <div className="px-5 pb-5 anim-slide-up space-y-3">
          {a.client_id && <ClientBirthdayBlock clientId={a.client_id} />}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Phone">{a.client_phone}</Field>
          <Field label="Booked at">{new Date(a.booked_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}</Field>
          <Field label="Entry">{a.entry_method}</Field>

          <Field label="Stylist / Therapist">
            <select className="h-10 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full"
                    value={a.assigned_stylist_id || ""} onChange={(e) => {
                      const s = staff.find((x) => x.id === e.target.value);
                      onAssign("assigned_stylist_id", s?.id || null);
                      onAssign("assigned_stylist_name", s?.name || null);
                    }}>
              <option value="">Unassigned</option>
              {staff.filter((s) => s.role !== "receptionist" && s.role !== "manager").map((s) => (
                <option key={s.id} value={s.id}>{`${s.name} · ${s.role}`}</option>
              ))}
            </select>
          </Field>
          <Field label="Chair / Room">
            <input value={a.chair_room || ""} onChange={(e) => onAssign("chair_room", e.target.value)} placeholder="e.g. Chair 3"
                   className="h-10 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full" />
          </Field>
          <Field label="Services requested">
            <div className="flex flex-wrap gap-1.5">
              {(a.services_requested || []).map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-full bg-[var(--color-gold-mid)]/12 text-[var(--color-gold-deeper)] font-inter font-medium text-[11.5px] border border-[var(--color-gold-mid)]/30">{s}</span>
              ))}
            </div>
          </Field>
          </div>

          {(canCancel || canReschedule) && (
            <div className="flex items-center gap-4 pt-3 mt-2 border-t border-white/40">
              {a.client_id && onOpenProfile && (
                <button
                  type="button"
                  onClick={onOpenProfile}
                  className="inline-flex items-center gap-1.5 font-inter text-[12.5px] font-medium text-[var(--color-gold-deeper)] hover:underline"
                  data-testid={`view-profile-link-${a.id}`}
                >
                  View profile
                </button>
              )}
              {canReschedule && (
                <button
                  type="button"
                  onClick={onRequestReschedule}
                  className="inline-flex items-center gap-1.5 font-inter text-[12.5px] font-medium text-[var(--color-mauve-deep)] hover:underline"
                  data-testid={RECEPTIONIST_EXTRA.rescheduleLink(a.id)}
                >
                  <CalendarClock size={13} /> Reschedule
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  onClick={onRequestCancel}
                  className="inline-flex items-center gap-1.5 font-inter text-[12.5px] font-medium text-[var(--color-danger-deep)] hover:underline"
                  data-testid={RECEPTIONIST_EXTRA.cancelLink(a.id)}
                >
                  <Ban size={13} /> Cancel
                </button>
              )}
              {a.rescheduled_from && (
                <span className="ml-auto font-inter text-[11px] text-[var(--color-text-muted)]">
                  Rescheduled from {new Date(a.rescheduled_from).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClientBirthdayBlock({ clientId }) {
  const [client, setClient] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/clients/${clientId}`).then((r) => { if (active) setClient(r?.data?.data); }).catch(() => {});
    return () => { active = false; };
  }, [clientId]);
  if (!client) return null;
  return <BirthdaySection client={client} testid={RECEPTIONIST_EXTRA.birthdayBadge(clientId)} />;
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-[10px] mb-1.5">{label}</div>
      <div className="font-inter text-[13.5px] text-[var(--color-text-primary)]">{children}</div>
    </div>
  );
}

// =========================
// Add Client Modal
// =========================
// =========================
// Smart Intake Ribbon — live golden alert pills as DOB / anniversary / location
// are entered. Powers the "wow" demo moment for sales — directly leverages
// birthday/anniversary cron + nearest-branch suggestion + VIP detection.
// =========================
function SmartIntakeRibbon({ client, dob, anniversary, location }) {
  const daysUntilMD = (mmdd) => {
    if (!mmdd) return null;
    const parts = mmdd.split("-");
    if (parts.length !== 3) return null;
    const [, m, d] = parts.map((x) => parseInt(x, 10));
    if (!m || !d) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let next = new Date(today.getFullYear(), m - 1, d);
    if (next < today) next = new Date(today.getFullYear() + 1, m - 1, d);
    return Math.round((next - today) / 86400000);
  };

  const dobDays = daysUntilMD(dob);
  const anniDays = daysUntilMD(anniversary);
  const isVipCandidate = (client?.total_visits || 0) >= 3 && !(client?.tags || []).includes("VIP");

  const branchHints = {
    "kalyani nagar": "Kalyani Nagar",
    "kalyani": "Kalyani Nagar",
    "viman nagar": "Kalyani Nagar",
    "viman": "Kalyani Nagar",
    "koregaon": "Koregaon Park",
    "camp": "Koregaon Park",
  };
  const matchedBranch = location
    ? Object.entries(branchHints).find(([k]) =>
        (location || "").toLowerCase().includes(k))?.[1]
    : null;

  const pills = [];
  if (dobDays !== null && dobDays <= 30) {
    pills.push({
      key: "dob",
      icon: "🎂",
      bg: "rgba(212,168,83,0.14)",
      border: "rgba(212,168,83,0.45)",
      color: "#8E6A1A",
      title: dobDays === 0 ? "Birthday today!" : `Birthday in ${dobDays} day${dobDays === 1 ? "" : "s"}`,
      sub: "Voucher will auto-send on the day — ₹500, valid 90 days.",
      testid: "smart-intake-dob",
    });
  }
  if (anniDays !== null && anniDays <= 30) {
    pills.push({
      key: "anni",
      icon: "💕",
      bg: "rgba(139,92,246,0.10)",
      border: "rgba(139,92,246,0.35)",
      color: "#7C3AED",
      title: anniDays === 0 ? "Anniversary today!" : `Anniversary in ${anniDays} day${anniDays === 1 ? "" : "s"}`,
      sub: "Auto-voucher fires on the day · ₹500 for 30 days.",
      testid: "smart-intake-anniversary",
    });
  }
  if (isVipCandidate) {
    pills.push({
      key: "vip",
      icon: "⭐",
      bg: "rgba(245,158,11,0.10)",
      border: "rgba(245,158,11,0.35)",
      color: "#B45309",
      title: `${client.total_visits} visits — mark as VIP?`,
      sub: `Lifetime spend ₹${(client.total_spend || 0).toLocaleString("en-IN")} · invite to memberships.`,
      testid: "smart-intake-vip",
    });
  }
  if (matchedBranch) {
    pills.push({
      key: "branch",
      icon: "🌟",
      bg: "rgba(94,64,96,0.10)",
      border: "rgba(94,64,96,0.30)",
      color: "#5E4060",
      title: `Closer to ${matchedBranch} branch?`,
      sub: "Mention our nearer location during checkout for next visit.",
      testid: "smart-intake-branch",
    });
  }
  if (client && (client.allergies || "").trim()) {
    pills.push({
      key: "allergy",
      icon: "⚠",
      bg: "rgba(185,28,28,0.08)",
      border: "rgba(185,28,28,0.35)",
      color: "#991B1B",
      title: `Allergy on file: ${client.allergies}`,
      sub: "Brief the stylist before service.",
      testid: "smart-intake-allergy",
    });
  }

  if (pills.length === 0) return null;
  return (
    <div className="rounded-2xl p-2.5 anim-fade-in" data-testid="smart-intake-ribbon"
         style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.55), rgba(212,168,83,0.06))",
                  border: "1px dashed rgba(212,168,83,0.35)" }}>
      <div className="flex items-center gap-1.5 mb-1.5 px-1">
        <span className="overline" style={{ color: "#8E6A1A" }}>Smart intake · revenue cues</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {pills.map((p) => (
          <div key={p.key}
               data-testid={p.testid}
               className="flex items-start gap-2 px-3 py-2 rounded-xl"
               style={{ background: p.bg, border: `1px solid ${p.border}` }}>
            <span className="text-[16px] leading-none mt-[1px]">{p.icon}</span>
            <div>
              <div className="font-sora font-semibold text-[12.5px]" style={{ color: p.color }}>{p.title}</div>
              <div className="font-inter text-[11px] leading-tight" style={{ color: p.color, opacity: 0.85 }}>{p.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReturningClientHero({ client, detail, onUseLastVisit }) {
  const initials = (client.name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase();
  const lastVisit = (detail?.history || [])[0];
  const lastServices = (lastVisit?.services_requested || []).filter(Boolean);
  const daysSince = client.last_visit_at
    ? Math.floor((Date.now() - new Date(client.last_visit_at).getTime()) / 86400000)
    : null;
  const visitWord = (client.total_visits || 0) === 1 ? "visit" : "visits";

  return (
    <div
      className="anim-slide-up rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(212,168,83,0.14) 0%, rgba(124,92,122,0.08) 100%)",
        border: "1.5px solid rgba(212,168,83,0.45)",
        boxShadow: "0 12px 28px -10px rgba(212,168,83,0.30)",
      }}
      data-testid="returning-client-hero"
    >
      {/* Soft sparkle accent */}
      <Sparkles
        size={56}
        className="absolute -top-3 -right-3 text-[var(--color-gold-mid)] opacity-15 pointer-events-none"
      />
      <div className="flex items-start gap-3 relative z-[1]">
        <div className="relative flex-shrink-0">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center font-sora font-bold text-white text-[15px]"
            style={{ background: "linear-gradient(135deg,#B08AAE,#7C5C7A,#5E4060)" }}
          >
            {initials}
          </div>
          {client.membership && (
            <div className="-mt-1.5 flex justify-center">
              <MemberBadge membership={client.membership} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="overline" style={{ color: "#B5892E" }}>Returning client</span>
            <Sparkles size={11} className="text-[var(--color-gold-deeper)]" />
          </div>
          <div className="font-sora font-semibold text-[15.5px] text-[var(--color-text-primary)] mt-0.5 truncate">
            Welcome back, {client.name?.split(" ")[0]}
          </div>
          <div className="font-inter text-[12px] text-[var(--color-text-muted)] mt-0.5">
            <span className="font-mono font-medium text-[var(--color-text-primary)]">
              {client.total_visits || 0}
            </span> {visitWord}
            {daysSince !== null && (
              <> · last seen <span className="font-mono font-medium text-[var(--color-text-primary)]">{daysSince}d</span> ago</>
            )}
            {client.preferred_stylist_name && (
              <> · prefers <span className="text-[var(--color-mauve-deep)] font-medium">{client.preferred_stylist_name}</span></>
            )}
          </div>
          {lastServices.length > 0 && (
            <div className="mt-2.5 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 font-inter text-[11.5px] text-[var(--color-text-muted)]">
                <History size={10} /> Last visit:
              </span>
              {lastServices.slice(0, 3).map((s) => (
                <span
                  key={s}
                  className="px-2 py-0.5 rounded-full font-inter text-[11px] font-medium"
                  style={{ background: "rgba(124,92,122,0.10)", color: "#5E4060" }}
                >{s}</span>
              ))}
              {lastServices.length > 3 && (
                <span className="font-inter text-[11px] text-[var(--color-text-muted)]">
                  +{lastServices.length - 3} more
                </span>
              )}
            </div>
          )}
          {lastServices.length > 0 && (
            <button
              type="button"
              onClick={() => onUseLastVisit(lastServices)}
              className="btn-3d btn-gold-3d btn-sm mt-3"
              data-testid="returning-client-use-last-visit"
            >
              <Zap size={13} /> Repeat last visit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddClientModal({ onClose, onCreate, services, staff, prefillPhone }) {
  const [form, setForm] = useState({
    client_name: "", client_phone: prefillPhone || "", gender: "female",
    entry_method: "Walk-in", services_requested: [],
    assigned_stylist_id: "", assigned_stylist_name: "", chair_room: "",
    branch_id: "branch-001",
    // Extended profile — captured at intake, optional
    dob: "", anniversary: "", email: "", occupation: "", location: "",
    address: "", referral_source: "", preferred_language: "",
  });
  const [conflict, setConflict] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [existingClient, setExistingClient] = useState(null);   // matched by phone (incl. membership + stats)
  const [existingDetail, setExistingDetail] = useState(null);   // last visits etc

  // Phone lookup — auto-fill if client already exists (uses /clients/search)
  useEffect(() => {
    const phone = form.client_phone.replace(/\D/g, "");
    if (phone.length < 10) {
      setExistingClient(null);
      setExistingDetail(null);
      return;
    }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/clients/search", { params: { phone } });
        if (!active) return;
        const list = unwrap(r) || [];
        const norm = (p) => (p || "").replace(/\D/g, "").slice(-10);
        const match = list.find((c) => norm(c.phone) === phone) || list[0];
        if (!match) {
          setExistingClient(null);
          setExistingDetail(null);
          return;
        }
        setExistingClient(match);
        setForm((f) => ({
          ...f,
          client_name: f.client_name || match.name || "",
          gender: f.gender,
        }));
        // Fetch full profile in parallel for last-visit services + extended fields
        try {
          const p = unwrap(await api.get(`/clients/${match.id}/profile`));
          if (active && p) {
            setExistingDetail(p);
            const c = p.client || {};
            setForm((f) => ({
              ...f,
              gender: c.gender || f.gender,
              dob: c.dob || f.dob,
              anniversary: c.anniversary || f.anniversary,
              email: c.email || f.email,
              occupation: c.occupation || f.occupation,
              location: c.location || f.location,
              address: c.address || f.address,
              referral_source: c.referral_source || f.referral_source,
            }));
          }
        } catch {}
      } catch {}
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [form.client_phone]);

  const grouped = useMemo(() => {
    const map = {};
    for (const s of services) {
      (map[s.category] = map[s.category] || []).push(s);
    }
    return map;
  }, [services]);

  // Re-check conflict whenever stylist changes
  useEffect(() => {
    if (!form.assigned_stylist_id) { setConflict(null); return; }
    let active = true;
    api.get(`/staff/${form.assigned_stylist_id}/availability`, { params: { branch_id: form.branch_id }})
      .then((r) => { if (active) setConflict(r?.data?.data?.available ? null : r?.data?.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [form.assigned_stylist_id, form.branch_id]);

  const submit = async () => {
    if (!form.client_name || !form.client_phone) { toast.error("Name and phone required"); return; }
    if (form.services_requested.length === 0) { toast.error("Select at least one service"); return; }
    const stylist = staff.find((s) => s.id === form.assigned_stylist_id);
    const phone = form.client_phone.startsWith("+91") ? form.client_phone : `+91 ${form.client_phone}`;
    try {
      await onCreate({
        ...form,
        client_phone: phone,
        assigned_stylist_id: stylist?.id || null,
        assigned_stylist_name: stylist?.name || null,
      });
      // Enrich the client profile after queue insertion — best-effort, non-blocking
      const enrichments = {
        dob: form.dob || null,
        anniversary: form.anniversary || null,
        email: form.email || null,
        occupation: form.occupation || null,
        location: form.location || null,
        address: form.address || null,
        referral_source: form.referral_source || null,
      };
      const hasEnrichment = Object.values(enrichments).some((v) => v);
      if (hasEnrichment) {
        try {
          const all = unwrap(await api.get("/clients")) || [];
          const norm = (p) => (p || "").replace(/\D/g, "").slice(-10);
          const target = all.find((c) => norm(c.phone) === norm(phone));
          if (target?.id) {
            await api.patch(`/clients/${target.id}`, enrichments);
          }
        } catch {}
      }
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Couldn't add to queue", { description: data?.code || "" });
    }
  };

  const toggleService = (name) => {
    setForm((f) => ({
      ...f,
      services_requested: f.services_requested.includes(name) ? f.services_requested.filter((s) => s !== name) : [...f.services_requested, name],
    }));
  };

  return (
    <ModalShell title="Add to queue" subtitle="A new client walking in or booking by phone." onClose={onClose} testid={RECEPTIONIST.addClientModal}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Full name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} />
        <Input label="Phone" prefix="+91" value={form.client_phone.replace("+91 ", "")} onChange={(v) => setForm({ ...form, client_phone: v.replace(/\D/g, "") })} />
      </div>

      {/* Returning client hero — appears when phone matches an existing client */}
      {existingClient && (
        <ReturningClientHero
          client={existingClient}
          detail={existingDetail}
          onUseLastVisit={(services) => {
            setForm((f) => ({ ...f, services_requested: services }));
            toast.success(`${services.length} service${services.length === 1 ? "" : "s"} pre-selected ✨`);
          }}
        />
      )}
      <PillRow label="Gender" options={["female", "male", "other"]} labels={{ other: "Prefer not to say" }} value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} />
      <PillRow label="Entry method" options={["Walk-in", "Call", "WhatsApp", "Appointment"]} value={form.entry_method} onChange={(v) => setForm({ ...form, entry_method: v })} />

      <div>
        <div className="overline mb-2">Services requested</div>
        <div className="space-y-3">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="font-sora font-semibold text-[11px] uppercase tracking-[0.2em] text-[var(--color-mauve)] mb-1.5">{cat}</div>
              <div className="flex flex-wrap gap-1.5">
                {list.map((s) => (
                  <button key={s.id} type="button" onClick={() => toggleService(s.name)}
                          className={`px-3 py-1.5 rounded-full text-[12px] font-inter font-medium border transition ${form.services_requested.includes(s.name) ? "bg-[var(--color-gold-mid)]/12 border-[var(--color-gold-mid)] text-[var(--color-gold-deeper)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}>
                    {form.services_requested.includes(s.name) && <Check size={11} className="inline mr-1" />}
                    {s.name} <span className="opacity-50">· ₹{s.base_price}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Preferred stylist">
          <select className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full"
                  value={form.assigned_stylist_id} onChange={(e) => setForm({ ...form, assigned_stylist_id: e.target.value })}>
            <option value="">Any available</option>
            {staff.filter((s) => s.role !== "receptionist" && s.role !== "manager").map((s) => (
              <option key={s.id} value={s.id}>{`${s.name} · ${s.role}${s.available_today ? "" : " (unavailable)"}`}</option>
            ))}
          </select>
        </Field>
        <Field label="Chair / Room (optional)">
          <input value={form.chair_room} onChange={(e) => setForm({ ...form, chair_room: e.target.value })} placeholder="e.g. Chair 3"
                 className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full" />
        </Field>
      </div>

      {conflict && (
        <div
          className="rounded-2xl p-4 mt-1"
          style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)" }}
          data-testid={RECEPTIONIST_EXTRA.conflictWarning}
        >
          <div className="flex items-start gap-2">
            <AlertOctagon size={16} className="text-[var(--color-warning-deep)] mt-0.5" />
            <div className="flex-1">
              <div className="font-sora font-semibold text-[13.5px] text-[var(--color-warning-deep)]">
                {staff.find((s) => s.id === form.assigned_stylist_id)?.name} is currently with {conflict.conflict?.with_client} ({conflict.conflict?.token_number}).
              </div>
              <div className="font-inter text-[12.5px] text-[var(--color-warning-deep)]/85 mt-0.5">
                Next available in ~{conflict.next_available_minutes || 30} minutes (including buffer).
              </div>
              {(conflict.alternatives || []).length > 0 && (
                <div className="mt-2">
                  <div className="overline text-[10px] mb-1">Available now</div>
                  <div className="flex flex-wrap gap-1.5">
                    {conflict.alternatives.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setForm({ ...form, assigned_stylist_id: a.id, assigned_stylist_name: a.name })}
                        className="px-2.5 py-1 rounded-full text-[11.5px] font-inter font-medium bg-white/80 border border-[var(--color-warning)]/30 text-[var(--color-warning-deep)] hover:bg-white"
                      >
                        {a.name} · {a.role}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Smart Intake Ribbon — fires live as DOB / anniversary / location are filled */}
      <SmartIntakeRibbon
        client={existingClient}
        dob={form.dob}
        anniversary={form.anniversary}
        location={form.location}
      />

      {/* More details — DOB, anniversary, occupation, location, etc. */}
      <button
        type="button"
        onClick={() => setMoreOpen((v) => !v)}
        data-testid="add-client-more-toggle"
        className="flex items-center gap-1.5 font-inter text-[12.5px] font-medium text-[var(--color-mauve-deep)] hover:underline w-fit"
      >
        {moreOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        {moreOpen ? "Hide" : "Add"} more client details
        {!moreOpen && <span className="font-inter text-[11px] text-[var(--color-text-muted)]">· DOB · Anniversary · Occupation · Location</span>}
      </button>
      {moreOpen && (
        <div className="rounded-2xl p-4 anim-slide-up space-y-3" style={{ background: "rgba(94,64,96,0.04)", border: "1px solid rgba(94,64,96,0.18)" }} data-testid="add-client-more-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date of birth">
              <input type="date" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })}
                     data-testid="add-client-dob-input"
                     className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-mono text-[13px] outline-none w-full" />
            </Field>
            <Field label="Anniversary">
              <input type="date" value={form.anniversary || ""} onChange={(e) => setForm({ ...form, anniversary: e.target.value })}
                     data-testid="add-client-anniversary-input"
                     className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-mono text-[13px] outline-none w-full" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Email (optional)">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="add-client-email-input"
                     placeholder="priya@example.com"
                     className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full" />
            </Field>
            <Field label="Occupation">
              <input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                     data-testid="add-client-occupation-input"
                     placeholder="Marketing Director, Bride-to-be, Student…"
                     list="occupations-list"
                     className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full" />
              <datalist id="occupations-list">
                {["Marketing", "Software Engineer", "Doctor", "Lawyer", "Teacher", "Architect", "Student", "Bride-to-be", "Entrepreneur", "Fashion Stylist", "HR Manager", "Banker"].map((o) => (
                  <option key={o} value={o} />
                ))}
              </datalist>
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Location / area">
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                     data-testid="add-client-location-input"
                     placeholder="Koregaon Park, Kalyani Nagar…"
                     list="locations-list"
                     className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full" />
              <datalist id="locations-list">
                {["Koregaon Park", "Kalyani Nagar", "Camp", "Aundh", "Viman Nagar", "Baner", "Wakad", "Hadapsar"].map((o) => (
                  <option key={o} value={o} />
                ))}
              </datalist>
            </Field>
            <Field label="Referral source">
              <select value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                      data-testid="add-client-referral-input"
                      className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full">
                <option value="">—</option>
                <option>Instagram</option>
                <option>Friend referral</option>
                <option>Walk-by</option>
                <option>Google</option>
                <option>Wedding planner</option>
                <option>WhatsApp</option>
                <option>Other</option>
              </select>
            </Field>
          </div>
          <Field label="Address (optional)">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                   placeholder="Flat, Lane, Society…"
                   className="h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none w-full" />
          </Field>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-2">
        <button className="btn-3d btn-glass-3d" onClick={onClose}>Cancel</button>
        <button className="btn-3d btn-gold-3d btn-lg" onClick={submit}>Add to queue</button>
      </div>
    </ModalShell>
  );
}

function EmergencyModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ client_name: "", client_phone: "", services_requested: ["Scalp soothing treatment"], notes: "" });
  return (
    <ModalShell title="Emergency walk-in" subtitle="This will be placed at the very top of the queue."
                onClose={onClose} accent="danger" testid={RECEPTIONIST.emergencyModal}>
      <div className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "rgba(185,28,28,0.08)", border: "1px solid rgba(185,28,28,0.2)" }} data-testid={RECEPTIONIST_EXTRA.emergencyOverrideBadge}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center anim-pulse-ring" style={{ background: "rgba(185,28,28,0.15)" }}>
          <AlertOctagon size={20} className="text-[var(--color-danger)]" />
        </div>
        <div className="font-inter text-[13px] text-[var(--color-danger-deep)]">
          Use for allergic reactions, urgent walk-ins, or any service that cannot wait. <span className="font-sora font-semibold uppercase tracking-[0.18em] text-[11px] ml-1">Emergency override · bypasses booking conflicts</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Full name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} />
        <Input label="Phone" prefix="+91" value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v.replace(/\D/g, "") })} />
      </div>
      <div>
        <div className="overline mb-1.5">Nature of issue</div>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                  placeholder="Describe what needs urgent attention…"
                  className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none" />
      </div>
      <div className="flex justify-end gap-3 mt-2">
        <button className="btn-3d btn-glass-3d" onClick={onClose}>Cancel</button>
        <button className="btn-3d btn-danger-3d btn-lg" onClick={() => onCreate({
          client_name: form.client_name || "Emergency walk-in",
          client_phone: `+91 ${form.client_phone || "00000 00000"}`,
          gender: "female",
          services_requested: form.services_requested,
          entry_method: "Emergency",
        })}>
          <AlertOctagon size={16} /> Add to top of queue
        </button>
      </div>
    </ModalShell>
  );
}

function BroadcastModal({ items, onClose }) {
  const [msg, setMsg] = useState("We are running approximately 15 minutes behind schedule. Thank you for your patience 🌷");
  const [audience, setAudience] = useState("waiting");
  const send = async () => {
    let n = 0;
    if (audience === "waiting") n = items.filter((a) => ["booked", "checked-in"].includes(a.status)).length;
    if (audience === "checked-in") n = items.filter((a) => a.status === "checked-in").length;
    if (audience === "booked") n = items.filter((a) => a.status === "booked").length;
    try {
      await api.post("/notifications/log", {
        type: "broadcast_delay",
        message: msg, channel: "whatsapp", status: "mocked",
        branch_id: "branch-001",
      });
    } catch {}
    toast.success(`WhatsApp broadcast sent`, { description: `Delivered to ${n} client${n === 1 ? "" : "s"} · simulated for demo.` });
    onClose();
  };
  return (
    <ModalShell title="Broadcast delay" subtitle="Send a WhatsApp to clients waiting today." onClose={onClose} testid={RECEPTIONIST.broadcastModal}>
      <div>
        <div className="overline mb-1.5">Message</div>
        <textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none" />
      </div>
      <PillRow label="Send to" options={["waiting", "checked-in", "booked"]}
               labels={{ waiting: "All Waiting", "checked-in": "Checked-in Only", booked: "Booked Today" }}
               value={audience} onChange={setAudience} />
      <div className="flex justify-end gap-3 mt-2">
        <button className="btn-3d btn-glass-3d" onClick={onClose}>Cancel</button>
        <button className="btn-3d btn-mauve-3d btn-lg" onClick={send}><Send size={16} /> Send broadcast</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, subtitle, children, onClose, accent = "default", testid }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }} data-testid={testid}>
      <div className="glass-elevated rounded-[24px] p-7 w-[min(620px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-1" style={accent === "danger" ? { color: "#B91C1C" } : {}}>{accent === "danger" ? "Emergency" : "Quick action"}</div>
            <h2 className="font-display text-[24px] font-bold tracking-[-0.02em]">{title}</h2>
            {subtitle && <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, prefix }) {
  return (
    <label className="block">
      <div className="overline mb-1.5">{label}</div>
      <div className={`flex items-center h-11 rounded-xl bg-white/70 border border-white/80 ${prefix ? "" : "px-3"}`}>
        {prefix && <div className="px-3 border-r border-white/80 font-mono text-[14px] text-[var(--color-text-muted)]">{prefix}</div>}
        <input value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 bg-transparent font-inter text-[14px] outline-none" />
      </div>
    </label>
  );
}

function PillRow({ label, options, labels = {}, value, onChange }) {
  return (
    <div>
      <div className="overline mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)}
                  className={`px-3 py-1.5 rounded-full text-[12.5px] font-inter font-medium border transition ${value === o ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}>
            {labels[o] || (o[0].toUpperCase() + o.slice(1))}
          </button>
        ))}
      </div>
    </div>
  );
}


// =========================
// Cancel Modal (380px)
// =========================
function CancelModal({ appointment, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }} data-testid={RECEPTIONIST_EXTRA.cancelModal}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(380px,calc(100vw-32px))] anim-slide-up-lg space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-1" style={{ color: "#B91C1C" }}>Cancel appointment</div>
            <h2 className="font-display text-[20px] font-bold tracking-[-0.02em]">{appointment.client_name}</h2>
            <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">{appointment.token_number} · {fmt(appointment.booked_at)}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>
        <div>
          <div className="overline mb-1.5">Reason (optional)</div>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Client unwell, double-booked, no longer needed…"
            className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none"
            data-testid={RECEPTIONIST_EXTRA.cancelReasonInput}
          />
        </div>
        <div className="rounded-xl p-3" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)" }}>
          <div className="font-inter text-[12px] text-[var(--color-warning-deep)]">
            Client will be notified via WhatsApp. This action cannot be undone.
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Keep appointment</button>
          <button className="btn-3d btn-danger-3d" onClick={() => onConfirm(reason)} data-testid={RECEPTIONIST_EXTRA.cancelConfirmBtn}>
            <Ban size={14} /> Confirm Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================
// Reschedule Modal (520px) — pill date row + 30-min slot grid + stylist select
// =========================
function RescheduleModal({ appointment, staff, branchId, onClose, onConfirm }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("");
  const [stylistId, setStylistId] = useState(appointment.assigned_stylist_id || "");
  const [stylistName, setStylistName] = useState(appointment.assigned_stylist_name || "");
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    const params = { date, branch_id: branchId };
    if (stylistId) params.stylist_id = stylistId;
    api.get(`/appointments/availability`, { params })
      .then((r) => { if (active) setAvailability(unwrap(r)); })
      .catch(() => { if (active) setAvailability(null); });
    return () => { active = false; };
  }, [date, branchId, stylistId]);

  const dates = useMemo(() => {
    const out = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 15; i++) {
      const d = new Date(today.getTime() + i * 86400000);
      out.push({
        iso: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }),
      });
    }
    return out;
  }, []);

  const slots = availability?.slots || [];
  const filteredStaff = (staff || []).filter((s) => !["receptionist", "manager"].includes(s.role));

  const submit = () => {
    if (!time) { toast.error("Please pick a new time slot"); return; }
    onConfirm({
      new_date: date,
      new_time: time,
      new_stylist_id: stylistId || null,
      new_stylist_name: stylistName || null,
      branch_id: branchId,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center anim-fade-in" style={{ background: "rgba(28,25,23,0.32)", backdropFilter: "blur(12px)" }} data-testid={RECEPTIONIST_EXTRA.rescheduleModal}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(560px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-1" style={{ color: "#5E4060" }}>Reschedule appointment</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">{appointment.client_name}</h2>
            <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">{appointment.token_number} · current {appointment.booked_at ? new Date(appointment.booked_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" }) : "—"}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <div>
          <div className="overline mb-1.5">Choose date</div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {dates.map((d) => (
              <button
                key={d.iso}
                type="button"
                onClick={() => { setDate(d.iso); setTime(""); }}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[12.5px] font-inter font-medium border transition ${date === d.iso ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}
                data-testid={RECEPTIONIST_EXTRA.rescheduleDateBtn(d.iso)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="overline mb-1.5">Choose stylist (optional)</div>
          <select
            value={stylistId}
            onChange={(e) => {
              const s = filteredStaff.find((x) => x.id === e.target.value);
              setStylistId(s?.id || "");
              setStylistName(s?.name || "");
              setTime("");
            }}
            className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none"
            data-testid={RECEPTIONIST_EXTRA.rescheduleStylistSelect}
          >
            <option value="">Any available stylist</option>
            {filteredStaff.map((s) => (
              <option key={s.id} value={s.id}>{`${s.name} · ${s.role}`}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="overline mb-1.5">Pick a time slot</div>
          {slots.length === 0 ? (
            <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] py-4 text-center">No slots loaded.</div>
          ) : (
            <div className="grid grid-cols-6 gap-1.5">
              {slots.map((s) => {
                const sel = time === s.time;
                return (
                  <button
                    key={s.time}
                    type="button"
                    disabled={!s.available}
                    onClick={() => setTime(s.time)}
                    className={`h-9 rounded-lg font-mono text-[12px] border transition ${sel ? "bg-[var(--color-gold-mid)]/15 border-[var(--color-gold-mid)] text-[var(--color-gold-deeper)]" : s.available ? "bg-white/70 border-white/80 text-[var(--color-text-secondary)] hover:bg-white" : "bg-white/30 border-white/40 text-[var(--color-text-faint)] line-through cursor-not-allowed"}`}
                    data-testid={RECEPTIONIST_EXTRA.rescheduleSlotBtn(s.time)}
                  >
                    {s.time}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} disabled={!time} data-testid={RECEPTIONIST_EXTRA.rescheduleConfirmBtn}>
            <CalendarClock size={14} /> Confirm Reschedule
          </button>
        </div>
      </div>
    </div>
  );
}
